import React from 'react'
import styled, { css } from 'styled-components'
import {
  ariaAttr,
  dataAttr,
  isNumber,
  mergeClassnames,
  useCallbackRef,
  useMergeRefs
} from '@mote/helpers'
import { KeyboardKey, __DEV__ } from '@mote/helpers/constants'
import { Block } from '../block/block'
import { CloseButton } from '../closeButton/closeButton'

/**
 * Taken from lodash
 *
 * @see Source   https://github.com/lodash/lodash/blob/master/escapeRegExp.js
 */
const reRegExpChar = /[\\^$.*+?()[\]{}|]/g
const reHasRegExpChar = RegExp(reRegExpChar.source)

function escapeRegExp(string) {
  return string && reHasRegExpChar.test(string)
    ? string.replace(reRegExpChar, '\\$&')
    : string || ''
}

function buildRegExpFromDelimiters(delimiters) {
  const delimiterChars = delimiters
    .map(delimiter => {
      const chrCode = delimiter - 48 * Math.floor(delimiter / 48)
      return String.fromCharCode(96 <= delimiter ? chrCode : delimiter)
    })
    .join('')
  const escapedDelimiterChars = escapeRegExp(delimiterChars)
  return new RegExp(`[${escapedDelimiterChars}]+`)
}

export const InputTag = React.forwardRef(
  (
    {
      name,
      disabled = false,
      placeholder,
      placeholderWithTags,
      label,
      htmlType = 'text',
      tags: _tags = [],
      delimiters: _delimeters = [],
      width: _width,
      maxWidth,
      fullWidth,
      htmlAutocomplete = 'off',
      readOnly = false,
      invalid = false,
      required = false,
      isClearable = false,
      allowDuplicates = false,
      allowAdditionFromPaste = true,
      allowDeleteFromEmptyInput = true,
      onClearClick: _onClearClick,
      onChange: _onChange,
      onFocus: _onFocus,
      onBlur: _onBlur,
      onTagRemove: _onTagRemove,
      onTagExists: _onTagExists,
      className,
      ...rest
    },
    ref
  ) => {
    const delimiters = React.useMemo(
      () => [..._delimeters, KeyboardKey.Enter],
      [_delimeters]
    )
    const [tags, setTags] = React.useState(_tags || [])
    const [isFocused, setIsFocused] = React.useState(false)
    const inputRef = React.useRef(null)
    const hasTags = React.useMemo(() => tags.length > 0, [tags])
    const refs = useMergeRefs(ref, inputRef)

    const onFocus = useCallbackRef(_onFocus)
    const onBlur = useCallbackRef(_onBlur)
    const onChange = useCallbackRef(_onChange)
    const onClearClick = useCallbackRef(_onClearClick)
    const onTagRemove = useCallbackRef(_onTagRemove)
    const onTagExists = useCallbackRef(_onTagExists)

    const _className = mergeClassnames('mote-input-tag-root', className)

    const width = React.useMemo(() => {
      if (fullWidth) return '100%'
      if (_width) return isNumber(_width) ? `${_width}px` : _width
      return 'initial'
    }, [fullWidth, _width])

    React.useEffect(() => {
      setTags(_tags || [])
    }, [_tags.join('')])

    React.useEffect(() => {
      if (disabled || readOnly) return
      onChange?.(tags)
    }, [tags])

    const handleFocus = React.useCallback(
      event => {
        setIsFocused(true)
        onFocus?.(event)
      },
      [onFocus]
    )

    const handleBlur = React.useCallback(
      event => {
        setIsFocused(false)
        onBlur?.(event)
      },
      [onBlur]
    )

    const handleClear = React.useCallback(
      event => {
        if (disabled || readOnly) return

        event.preventDefault()
        event.stopPropagation()
        event.nativeEvent.stopImmediatePropagation()

        setTags([])
        onClearClick?.(event)

        if (!inputRef.current) return

        inputRef.current.focus()
      },
      [disabled, onClearClick, readOnly]
    )

    const handleOnPaste = React.useCallback(
      event => {
        if (!allowAdditionFromPaste) return

        event.preventDefault()

        const clipboardData = event.clipboardData || window.clipboardData
        const pastedText = clipboardData.getData('text')

        const delimiterRegExp = buildRegExpFromDelimiters(delimiters)
        let newTags = pastedText.split(delimiterRegExp)

        newTags = [...new Set(newTags)].map(text => text.trim()).filter(Boolean)

        if (!allowDuplicates)
          newTags = newTags.filter(tag => !tags.includes(tag))

        if (newTags.length === 0) return

        setTags(prev => [...prev, ...newTags])
      },
      [allowAdditionFromPaste, delimiters, allowDuplicates, tags]
    )

    const handleContainerClick = React.useCallback(
      event => {
        if (disabled || readOnly) return

        event.stopPropagation()
        event.preventDefault()
        event.nativeEvent.stopImmediatePropagation()

        inputRef.current?.focus()
      },
      [disabled, readOnly]
    )

    const handleOnKeyUp = React.useCallback(
      event => {
        event.stopPropagation()

        const text = event.target.value.trim()

        if (
          allowDeleteFromEmptyInput &&
          event.keyCode === KeyboardKey.Backspace &&
          hasTags &&
          !text
        ) {
          setTags(prev => prev.slice(0, -1))
        }

        if (text && delimiters.includes(event.keyCode)) {
          if (!allowDuplicates && tags.includes(text)) {
            onTagExists?.(text, event)
            return
          }
          setTags([...tags, text])
          event.target.value = ''
          event.preventDefault()
        }
      },
      [
        tags,
        hasTags,
        onTagExists,
        delimiters,
        allowDuplicates,
        allowDeleteFromEmptyInput
      ]
    )

    const handleOnTagRemove = React.useCallback(
      (event, text) => {
        event.preventDefault()
        event.stopPropagation()
        event.nativeEvent.stopImmediatePropagation()

        setTags(prev => prev.filter(tag => tag !== text))
        onTagRemove?.(text)
        inputRef.current?.focus()
      },
      [onTagRemove]
    )

    return (
      <Root
        className={_className}
        width={width}
        maxWidth={maxWidth}
        data-disabled={dataAttr(disabled)}
      >
        {label && <LabelWrapper>{label}</LabelWrapper>}
        <InputWrapper className='mote-input-tag-wrapper'>
          <InputGroup
            as={label ? 'div' : 'label'}
            className='mote-input-tag-group'
            onClick={handleContainerClick}
            data-focused={dataAttr(isFocused)}
            data-disabled={dataAttr(disabled)}
            data-invalid={dataAttr(invalid)}
          >
            {tags.map(tag => (
              <Tag
                key={tag}
                text={tag}
                onRemove={event => handleOnTagRemove(event, tag)}
                disabled={disabled}
              />
            ))}
            <StyledInput
              ref={refs}
              className='mote-input'
              name={name}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyUp={handleOnKeyUp}
              onPaste={handleOnPaste}
              placeholder={hasTags ? placeholderWithTags : placeholder}
              type={htmlType}
              autoCorrect='off'
              autoCapitalize='off'
              spellCheck='false'
              autoComplete={htmlAutocomplete}
              readOnly={readOnly}
              invalid={invalid}
              aria-invalid={ariaAttr(invalid)}
              aria-required={ariaAttr(required)}
              aria-readonly={ariaAttr(readOnly)}
              aria-placeholder={ariaAttr(placeholder)}
              disabled={disabled}
              {...rest}
            />
            {isClearable && (
              <InputClearButton
                className='mote-input__clear-button'
                visible={hasTags}
                disabled={disabled || readOnly}
                onClick={handleClear}
              />
            )}
          </InputGroup>
        </InputWrapper>
      </Root>
    )
  }
)

if (__DEV__) {
  InputTag.displayName = 'InputTag'
}

const Tag = ({ text, onRemove, disabled }) => {
  return (
    <TagRoot>
      <span>{text}</span>
      {!disabled && (
        <CloseButton size={24} onClick={onRemove} style={{ marginRight: -8 }} />
      )}
    </TagRoot>
  )
}

const Root = styled.div`
  width: ${({ width }) => width};
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  box-sizing: border-box;

  max-width: ${({ maxWidth }) =>
    isNumber(maxWidth) ? `${maxWidth}px` : maxWidth};

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledInput = styled.input`
  width: 50%;
  height: 28px;
  min-width: 0;
  padding: 0;
  font-weight: normal;
  font-size: 15px;
  line-height: 120%;
  color: #232323;
  appearance: none;
  border: none;
  border-radius: 0;
  outline: none;
  background: transparent;
  transition: all 150ms ease-out;

  &::placeholder {
    color: #b2b2cc;
    font-weight: normal;
    text-transform: none;
    transition: opacity 150ms ease;
  }

  &:focus {
    &::placeholder {
      opacity: 0;
    }
  }

  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
  }
`

const LabelWrapper = styled.label`
  font-weight: 500;
  font-size: 15px;
  line-height: 120%;
  color: #232323;
  margin-bottom: 8px;
`

const InputWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  min-height: 42px;
`

const InputGroup = styled.div`
  height: 100%;
  display: inline-flex;
  flex-direction: row;
  gap: 8px;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  vertical-align: middle;
  user-select: none;
  padding: 6px;
  border-radius: 4px;
  border: 1px solid #d1d1e0;
  background: #ffffff;
  transition: all 150ms ease;
  box-sizing: border-box;
  cursor: text;

  &:hover {
    border-color: #b2b2cc;
  }

  &[data-focused] {
    outline: none;
    border: 1px solid #ac0ae8;
    box-shadow: 0 0 0 1px #ac0ae8;

    &::placeholder {
      opacity: 0;
    }
  }

  &[data-disabled] {
    pointer-events: none;
    cursor: not-allowed;
  }

  &[data-invalid]:not([data-disabled]) {
    border-color: #f44336;

    &:hover {
      border-color: #f44336;
    }

    &[data-focused] {
      border-color: #f44336;
      box-shadow: 0 0 0 1px #f44336;
    }
  }
`

const InputClearButton = styled(CloseButton).attrs({
  size: 24,
  'aria-label': 'Clear'
})`
  position: absolute;
  right: 6px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  border-radius: 100%;
  color: #646f7b;
  opacity: 0;
  visibility: hidden;
  transform: translateX(20%);
  transition-duration: 150ms;
  transition-timing-function: ease;
  transition-property: color, background-color, opacity, transform;

  ${({ visible }) =>
    visible &&
    css`
      visibility: visible;
      transform: translateX(0);
      opacity: 1;
    `}
`

const TagRoot = styled(Block).attrs({
  role: 'button',
  layout: 'horizontal',
  align: 'center',
  gap: 8
})`
  color: #232323;
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 120%;
  padding: 0 8px;
  background: #f6f6f9;
  border-radius: 4px;
  width: max-content;
  height: 28px;
  cursor: default;
`
